import axios from "axios";
import { deriveErrorMessage } from "libLegacy/internal/deriveErrorMessage";
import {
  DashboardBillingValues,
  DashboardBillingChartsValue,
  FilterDashboard,
  CarbonFootprintResult,
  InvestmentsResult,
  Scene,
  Environment,
  ConsumerMonitoringData,
  MonitoringFilters_V2,
  DashboardMonitoringValues_V2,
  DeviceAC,
} from "./models";
import { ItemResponse } from "./axios";
import { ptBR } from "date-fns/locale";

export const fetchDashboardBillinBrief = async (
  token: string,
  filter: Partial<FilterDashboard>,
  contracts: string
) => {
  const { month, year, granularity } = filter;
  const params = { month, year, granularity };

  if (contracts) {
    params["contractId"] = contracts;
  }

  return axios
    .get<ItemResponse<DashboardBillingValues>>(
      `${process.env.REACT_APP_API_PATH}/dashboard/billing/brief`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      }
    )
    .then((r) => {
      return r.data.data.item;
    })
    .catch((e) => {
      const err = deriveErrorMessage(e, "dash6");
      return Promise.reject(err);
    });
};

export const fetchInvoicePDF = async (
  token: string,
  pdfUrl: string
): Promise<string> => {
  return axios
    .get(`${process.env.REACT_APP_API_PATH}${pdfUrl}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: "arraybuffer",
    })
    .then((res) => {
      const fileBlob = new Blob([res.data], { type: "application/pdf" });
      return URL.createObjectURL(fileBlob);
    });
};

export const fetchDashboardBillingCharts_V2 = async (
  token: string,
  from: Date,
  to: Date,
  contracts: string
) => {
  const params = {
    fromMonth: from.toISOString().substring(0, 10),
    toMonth: to.toISOString().substring(0, 10),
  };

  if (contracts) {
    params["contractId"] = contracts;
  }

  return axios
    .get<DashboardBillingChartsValue>(
      `${process.env.REACT_APP_API_PATH}/dashboard/billing/graph`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      }
    )
    .then((r) => {
      return r.data;
    })
    .catch((e) => {
      const err = deriveErrorMessage(e, "dash6");
      return Promise.reject(err);
    });
};

export const fetchDashboardMonitoringBrief_V2 = async (
  token: string,
  filter: MonitoringFilters_V2
) => {
  return axios
    .get<DashboardMonitoringValues_V2>(
      `${process.env.REACT_APP_API_PATH}/2.0/dashboard/monitoring/generated-energy`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filter,
      }
    )
    .then(({ data }) => {
      return data;
    })
    .catch((e) => {
      const err = deriveErrorMessage(e, "dash12");
      return Promise.reject(err);
    });
};

export const fetchCarbonFootprint = async (
  token: string,
  spaceId: string
): Promise<CarbonFootprintResult> => {
  return axios
    .get(
      `${process.env.REACT_APP_API_PATH}/carbon_footprint?space_id=${spaceId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .then(({ data }) => {
      const { co2_avoided, kilometers_not_driven, trees_planted } = data;
      return {
        co2Avoided: co2_avoided,
        kilometersNotDriven: kilometers_not_driven,
        treesPlanted: trees_planted,
      };
    });
};

export const fetchInvestments = async (
  token: string
): Promise<InvestmentsResult> => {
  return axios
    .get(`${process.env.REACT_APP_API_PATH}/investment`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then(({ data }) => {
      return data;
    });
};

export const updateInstallationDate = (
  token: string,
  installationDate: string
) => {
  const data = { installationDate };
  return postUnitInvestment(token, data);
};

export const updateInvestmentData = (
  token: string,
  expectedMonthlyEconomyBrl: number,
  investedAmountBrl: number
) => {
  const data = {
    investedAmountBrl: investedAmountBrl.toString(),
    expectedMonthlyEconomyBrl: expectedMonthlyEconomyBrl.toString(),
  };
  // @ts-ignore legacy API, to be deleted
  return postUnitInvestment(token, data);
};

const postUnitInvestment = async (
  token: string,
  data:
    | { installationDate: string }
    | Record<"investedAmountBrl" | "expectedMonthlyEconomyBrl", number>
) => {
  return axios
    .post(`${process.env.REACT_APP_API_PATH}/unit/investment`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then(({ data }) => {
      return data;
    });
};

export const updateEstimatedEnergy = async (
  token: string,
  estimatedEnergyKwh: number
) => {
  const data = {
    estimatedEnergyKwh: estimatedEnergyKwh,
  };
  return axios
    .put(`${process.env.REACT_APP_API_PATH}/unit/estimated-energy`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then(({ data }) => {
      return data;
    });
};

export const fetchSceneList = async (
  token: string,
  spaceId: string
): Promise<Array<Scene>> => {
  return axios
    .get(`${process.env.REACT_APP_API_PATH}/automation/${spaceId}/scenes`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then(({ data }) => {
      return data.scenes || [];
    });
};

export const updateSceneStatus = async (
  token: string,
  spaceId: string,
  sceneId: string,
  newStatus: boolean
) => {
  const body = { isActive: newStatus };

  return axios
    .put(
      `${process.env.REACT_APP_API_PATH}/automation/${spaceId}/scene/${sceneId}/toggle`,
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .then(({ data }) => {
      return data;
    });
};

export const deleteScene = async (
  token: string,
  spaceId: string,
  sceneId: string
) => {
  return axios
    .put(
      `${process.env.REACT_APP_API_PATH}/automation/${spaceId}/scene/${sceneId}/delete`,
      {},
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then(({ data }) => {
      return data;
    });
};

export const fetchEnvironmentStatus = async (
  token: string,
  spaceId: string
): Promise<Array<Environment>> => {
  return axios
    .get(
      `${process.env.REACT_APP_API_PATH}/automation/${spaceId}/environments`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .then(({ data }) => {
      return data.environments;
    });
};

export const fetchDecarbProfileUrl = async (token: string): Promise<string> => {
  const today = new Date();
  const month = today.toLocaleDateString(ptBR.code, { month: "2-digit" });
  const year = today.toLocaleDateString(ptBR.code, { year: "numeric" });

  return axios
    .get(`${process.env.REACT_APP_API_PATH}/users/descabonization/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: { month, year },
    })
    .then(({ data: { url } }) => {
      return url;
    });
};

export const fetchConsumerMonitoring = async (
  token: string,
  granularity: string,
  spaceId: string,
  start: Date,
  end?: Date
): Promise<ConsumerMonitoringData> => {
  const start_time = start
    .toISOString()
    .substring(0, 10)
    .split("-")
    .reverse()
    .join("-");
  const end_time =
    end?.toISOString().substring(0, 10).split("-").reverse().join("-") || null;

  const params = { start_time, end_time, granularity };
  const pathSuffix = granularity === "day" ? "-day" : "";

  if (spaceId) {
    params["space_id"] = spaceId;
  }

  return axios
    .get(
      `${process.env.REACT_APP_API_PATH}/dashboard/monitoring/smart-meter${pathSuffix}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      }
    )
    .then(({ data: { consumption } }) => {
      return consumption;
    });
};

export const createNewUser = async (
  formData: Record<"name" | "email" | "password", string>
) => {
  const body = formData;

  return axios
    .post(`${process.env.REACT_APP_API_PATH}/users/create`, body, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then(({ data: { data } }) => {
      return data;
    });
};

export const deleteUser = async (userId, token) => {
  return axios.delete(`${process.env.REACT_APP_API_PATH}/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const fetchACList = async (
  token: string,
  rootSpace: string
): Promise<Array<DeviceAC>> => {
  return axios
    .get(
      `${process.env.REACT_APP_API_PATH}/automation/air-conditioning/${rootSpace}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .then(({ data }) => {
      return data.data.items;
    });
};
